/**
 * How dictated words join the text already in a box (2 Nov 2026 — an alpha
 * tester's spoken list arrived as ONE task). Two rules:
 *
 * 1. Line breaks survive. The old join collapsed ALL whitespace, newlines
 *    included, so even a list typed line by line became one line the moment
 *    the mic was used.
 * 2. Each tap of the mic starts a NEW LINE, and saying "next item" (or
 *    "next task", "new item", "new line") starts one too — so a list
 *    can be spoken item by item and every item is its own line, which both
 *    the AI and the on-device sort treat as its own task.
 */

/** Spoken separators: "next item", "next task", "new item", "new line".
 *  Bare "next" is NOT one — "next week", "what's next" are task words. */
const SPOKEN_BREAK = /[ \t]*[.,]?[ \t]*\b(?:next|new)[ \t]+(?:item|task|line)\b[.,]?[ \t]*/gi;

/** Collapse runs of spaces/tabs only — never line breaks. */
export function tidyDictation(text: string): string {
  return text
    .replace(/[ \t]+/g, ' ')
    .replace(/ *\n */g, '\n')
    .replace(/\n{2,}/g, '\n');
}

/** A spoken utterance with its separators turned into line breaks. A break
 *  that ENDS an utterance is kept, so the next utterance starts a new line. */
export function spokenBreaks(utterance: string): string {
  return utterance.replace(SPOKEN_BREAK, '\n').replace(/^\n+/, '');
}

/** The base a new mic session appends to: existing text, then a new line. */
export function dictationBase(currentText: string): string {
  const t = tidyDictation(currentText).trim();
  return t ? `${t}\n` : '';
}

/** The box's text: the base plus this session's words, line breaks kept
 *  (trailing spaces trimmed; a trailing break kept so the next words land
 *  on a new line). */
export function joinDictation(base: string, session: string): string {
  return tidyDictation(`${base}${spokenBreaks(session)}`).replace(/ +$/, '');
}

/** The base after an utterance ends while the mic stays on (Android): the
 *  next utterance continues the same line unless a break was spoken. */
export function continueBase(merged: string): string {
  if (!merged) return '';
  return merged.endsWith('\n') ? merged : `${merged} `;
}

/** Shown under the brain-dump box while the mic is on — the rule above, in
 *  the user's words (CLAUDE.md §2: no state looks like "nothing happened"). */
export const DICTATION_HINT =
  'Listening… say “next item” between items, or tap the mic again for each one — every line becomes its own task.';

/**
 * Quick Task (the one-line box on the board) adds one task per line — and
 * since a one-line box can't show lines, a spoken "next item" between tasks
 * adds several, all in the chosen zone (Chad, 2 Nov 2026: he said "next"
 * between items there and got one task). Text with no separator is one task,
 * exactly as before.
 */
export function splitQuickTasks(text: string): string[] {
  return spokenBreaks(text.replace(/\r?\n/g, ' next item '))
    .split('\n')
    .map((t) => t.replace(/\s+/g, ' ').trim())
    .filter(Boolean);
}

/** Shown under Quick Task while its mic is on. */
export const QUICK_DICTATION_HINT =
  'Listening… say “next item” between tasks to add several to this zone.';
